import { mapState, mapGetters } from 'vuex'
import { SURVEY_SAPPORO, INFO_QUESTIONS, SET_LOADING, SET_QUESTION_ANSWER, GET_DATA_IDX_BY_TYPE, SET_FILES_FOR_DELETE, FETCH_DOWNLOAD_FILE } from '@/store/actions/survey'
import { getFullTextByType, getMessage } from '@/utils/survey'
import { validateFile, uploadFileToS3 } from '@/utils/fileUpload'

export default {
  props: {
    participant: {
      type: Object,
      required: true
    },
    sqCode: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      s3BucketUrl: `https://${process.env.AWS_BUCKET_NAME}`
    }
  },
  computed: {
    ...mapState(SURVEY_SAPPORO, {
      sCode: state => state.s_code,
      waveNumber: state => state.wave_number,
      aboUid: state => state.abo_uid
    }),
    ...mapGetters(SURVEY_SAPPORO, {
      questions: INFO_QUESTIONS,
      getIdx: GET_DATA_IDX_BY_TYPE
    }),
    resIdx() {
      return this.getIdx(this.participant.type, this.participant.type_seq);
    },
    question() {
      return this.questions[this.sqCode];
    },
    s3FilePath() {
      return `survey/${this.sCode}/${this.waveNumber}/${this.aboUid}/${getFullTextByType(this.participant, 'en')}`;
    },
    fileData() {
      return this.participant.questions[this.sqCode] || {};
    },
    path() {
      return this.fileData.path;
    },
    originalName() {
      return this.fileData.original_name
    },
    existFileData() {
      return !!this.fileData.path && !!this.fileData.original_name;
    },
    downloadUrl() {
      return this.existFileData ? `${this.s3BucketUrl}/${this.path}/${this.originalName}` : '';
    }
  },
  methods: {
    // 파일 업로드 버튼 클릭
    clickUploadButton(e) {
      e.preventDefault();
      if(this.disabled || this.existFileData) return false;
      this.$refs.attachment.click();
    },
    // AWS S3 Bucket에 파일 업로드
    async uploadAttachment(e) {
      e.preventDefault();
      const file = e.target.files[0];
      if(!file) return false;

      // 1. 파일 체크
      const errMsg = validateFile(file);
      if(errMsg) {
        alert(errMsg);
        return false;
      }

      try {
        this.$store.commit(`${SURVEY_SAPPORO}/${SET_LOADING}`, true);
        // 2. 파일명 설정
        const fileName = this.getFileName(file);
        // 3. 파일 S3 업로드
        await uploadFileToS3(`${this.s3FilePath}/${fileName}`, file);
        // 4. 업로드 된 파일 데이터 저장
        this.setFileData(this.s3FilePath, fileName);
      } catch (error) {
        alert(getMessage('ALERT_RETRY'));
        console.error('Upload Attachment ERROR!!', error.stack);
        return;
      } finally {
        this.$store.commit(`${SURVEY_SAPPORO}/${SET_LOADING}`, false);
      }
    },
    // AWS S3 Bucket에 저장될 파일명 설정
    getFileName(file) {
      // MAC OS에서 한글 파일명이 NFD 방식으로 저장이 될 수 있기에 NFC로 인코딩
      const fileName = file.name.normalize();
      // 고객이 업로드한 파일명에 현재 시각 밀리세컨드를 붙임(동일한 파일명으로 업로드가 되는 것을 방지하기 위함)
      // return fileName.substring(0, fileName.lastIndexOf('.')) + '_' + new Date().getTime() + fileName.substring(fileName.lastIndexOf('.'));
      
      // 파일명 제거하고 timestamp + 난수(문자2) + 난수(숫자2) + 파일확장자
      return new Date().getTime() + this.getRandomWord(2) + fileName.substring(fileName.lastIndexOf('.'));
    },
    getRandomWord(length) {
      const charsWord = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
      const charsDigit = '1234567890';
      let str = '';    
      for (let i = 0; i < length; i++) {
        str += charsWord.charAt(Math.floor(Math.random() * charsWord.length));
      }    
      for (let i = 0; i < length; i++) {
        str += charsDigit.charAt(Math.floor(Math.random() * charsDigit.length));
      }    
      return str; 
    },
    /**
     * 첨부 파일 삭제
     * - 기존에는 삭제 시 바로 S3에서도 삭제했으나,
     * - 설문을 저장하지 않고 넘어갈 시 첨부파일이 삭제되지 않게 하기 위해
     * - 설문을 저장하는 시점에 S3에서 기존 파일을 일괄 삭제하는 것으로 변경
     */
    async deleteAttachment(e) {
      e.preventDefault();
      // 파일 삭제 전 경고창
      if(!confirm(getMessage('CONFIRM_DELETE_FILE'))) {
        return;
      }

      this.$store.commit(`${SURVEY_SAPPORO}/${SET_FILES_FOR_DELETE}`, `${this.path}/${this.originalName}`);
      this.resetFileData();
    },
    async downloadFile(e) {
      e.preventDefault();
      if(e.target.value && this.downloadUrl) {
        this.$store.commit(`${SURVEY_SAPPORO}/${SET_LOADING}`, true);
        
        // abo_uid 는 전송하지만, local 인 경우에만 사용이 되고, dev,prod 인 경우는 credencials를 사용합니다. 
        const b64Data = await this.$store.dispatch(`${SURVEY_SAPPORO}/${FETCH_DOWNLOAD_FILE}`
                                                  , {s_code: this.participant.s_code, wave_number: this.participant.wave_number
                                                    , sr_seq: this.participant.sr_seq, sq_code: this.sqCode
                                                    , filename: this.fileData.original_name, abo_uid: this.participant.abo_uid});

        // 파일이 존재하는 경우만 다운로드 진행 
        if (b64Data) {     
          /* 새탭으로 이미지 출력
          const newWindow = window.open(); //새탭에서 열기
          newWindow.document.write('<html><head Content-Type: application/octet-stream><title>미리보기</title></head><body><img src="data:image/png;base64,' + b64Data + '"></body></html>');
          */

          /* 파일 다운로드 */
          const uri = 'data:image/png;base64,' + b64Data;
          const link = document.createElement('a');
          link.download = this.originalName; // 파일 이름 지정
          link.href = uri;
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link); // 추가한 a 태그 삭제
        }

        setTimeout(() => { // 연속 파일 다운로드 클릭 시 버그 수정
          this.$store.commit(`${SURVEY_SAPPORO}/${SET_LOADING}`, false);
        }, 500);

        return false;
      }
    },
    setFileData(path, originalName) {
      this.$store.commit(`${SURVEY_SAPPORO}/${SET_QUESTION_ANSWER}`, {
        idx: this.resIdx,
        sq_code: this.sqCode,
        original_name: originalName,
        path,
        hasError: !originalName || !path
      });
    },
    resetFileData() {
      this.$store.commit(`${SURVEY_SAPPORO}/${SET_QUESTION_ANSWER}`, {
        idx: this.resIdx,
        sq_code: this.sqCode,
        original_name: null,
        path: null,
        hasError: false
      });
    }
  },
}


// WEBPACK FOOTER //
// ./src/views/surveys/sapporo/mixins/AttachmentMixin.js